import { useEffect, useMemo, useState } from 'react'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'
import { useNavigate, useParams } from 'react-router'
import { useAgentContext } from '@klear/ui/contexts/agent-context'
import { Loader2 } from 'lucide-react'
import { PERMISSION_ACTION_EDIT, PERMISSION_ENTITY_SOURCE_DOCUMENT, ROUTE_DOCUMENT_REVIEW } from '@klear/constants'
import type { NextStep } from '@klear/schema/document-review-step'
import { Alert, AlertDescription } from '@klear/ui/components/alert'
import { Button } from '@klear/ui/components/button'
import { Combobox, type ComboboxOption } from '@klear/ui/components/combobox'
import { Label } from '@klear/ui/components/label'
import { Skeleton } from '@klear/ui/components/skeleton'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useTraders } from '@klear/ui/hooks/use-traders'
import { DocumentPane } from '@/app/document-review/document-pane'
import { BackLink } from '@/app/document-review/document-review-workspace'
import { NextStepBadge } from '@/app/document-review/next-step-badge'
import { RejectModal } from '@/app/document-review/reject-modal'
import { useDocumentReviewDocument } from '@/app/document-review/use-document-review'
import { useDocumentReviewActions } from '@/app/document-review/use-document-review-actions'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

const PROCESSING_START_CAP_MS = 60000

const MISSING_DOCUMENT_CODES = ['NOT_FOUND', 'BAD_REQUEST']

export default function DocumentReviewNotProcessed() {
  const { documentKey = '' } = useParams<{ documentKey: string }>()
  useAgentContext([`Document ${documentKey}.`])
  return <NotProcessedDocument key={documentKey} documentKey={documentKey} />
}

function NotProcessedDocument({ documentKey }: { documentKey: string }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { traders, isLoading: loadingTraders } = useTraders()
  const canEdit = user.hasPermission(PERMISSION_ENTITY_SOURCE_DOCUMENT, PERMISSION_ACTION_EDIT)
  const { process, deny } = useDocumentReviewActions()
  const [rejectOpen, setRejectOpen] = useState(false)
  const [processingFor, setProcessingFor] = useState<string | null>(null)
  const [didNotStart, setDidNotStart] = useState(false)
  const [pickedTraderId, setPickedTraderId] = useState<string | null>(null)
  const query = useDocumentReviewDocument(documentKey, { poll: processingFor !== null })
  const sourceDocument = query.data
  useDocumentTitle(
    sourceDocument?.uploadedFilename,
    'Documents',
    traders?.find((trader) => trader.id === sourceDocument?.traderId)?.name
  )

  const traderOptions = useMemo<ComboboxOption[]>(
    () => traders?.map((trader) => ({ value: toString(trader.id), label: trader.name || 'Unknown' })) ?? [],
    [traders]
  )

  const runId = sourceDocument?.runId ?? null
  const documentId = sourceDocument?.documentId ?? null
  useEffect(() => {
    if (runId !== null) void navigate(`/${ROUTE_DOCUMENT_REVIEW}/${runId}`, { replace: true })
    else if (documentId !== null && toString(documentId) !== documentKey) {
      void navigate(`/${ROUTE_DOCUMENT_REVIEW}/document/${documentId}`, { replace: true })
    }
  }, [runId, documentId, documentKey, navigate])

  useEffect(() => {
    if (processingFor === null) return
    const giveUp = setTimeout(() => {
      setProcessingFor(null)
      setDidNotStart(true)
    }, PROCESSING_START_CAP_MS)
    return () => clearTimeout(giveUp)
  }, [processingFor])

  if (sourceDocument === undefined) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <BackLink />
        {query.error === null ? (
          <Skeleton className="h-full w-full" />
        ) : (
          <Alert variant="destructive">
            <AlertDescription>
              {MISSING_DOCUMENT_CODES.includes(query.error.data?.code ?? '')
                ? "This document can't be found."
                : "This document couldn't be loaded. Try again in a moment."}
            </AlertDescription>
          </Alert>
        )}
      </div>
    )
  }

  const traderId = pickedTraderId ?? toString(sourceDocument.traderId)
  const traderName = traders?.find((trader) => toString(trader.id) === traderId)?.name ?? null
  const queued = processingFor === null && sourceDocument.queued
  const step: NextStep = processingFor === null && !queued ? 'notProcessed' : 'processing'

  const processDocument = () => {
    if (traderName === null) return
    setDidNotStart(false)
    setProcessingFor(traderName)
    process.mutate(
      { documentId: sourceDocument.documentId, traderId: toNumber(traderId) },
      {
        onError: () => setProcessingFor(null),
        onSuccess: (result) => {
          if (result.outcome === 'disabled') setProcessingFor(null)
        }
      }
    )
  }

  const rejectDocument = (input: { reason: string }) => {
    deny.mutate(
      { documentId: sourceDocument.documentId, reason: input.reason },
      { onSuccess: () => setRejectOpen(false) }
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <div className="flex shrink-0 flex-wrap items-baseline gap-x-3 gap-y-1">
        <BackLink />
        <h1 className="text-xl font-semibold">{sourceDocument.uploadedFilename ?? 'Source document'}</h1>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[52fr_48fr]">
        <div className="min-h-0 lg:h-full">
          <DocumentPane
            documentId={sourceDocument.documentId}
            uploadedFilename={sourceDocument.uploadedFilename}
            highlights={[]}
            activeKey={null}
          />
        </div>

        <div className="flex min-h-0 flex-col gap-3">
          {query.error !== null && (
            <Alert className="shrink-0">
              <AlertDescription>Can&apos;t reach Klear right now. Retrying.</AlertDescription>
            </Alert>
          )}

          <section className="bg-card rounded-lg border p-4" aria-live="polite">
            <NextStepBadge step={step} />
            {queued ? (
              <>
                <h2 className="mt-2 text-base font-medium">Queued for processing</h2>
                <p className="text-muted-foreground mt-1 text-sm">
                  It was sent for processing and is waiting its turn. Its review opens here once processing starts.
                </p>
                <div className="mt-4">
                  <Button variant="outline" onClick={() => setRejectOpen(true)} disabled={!canEdit || deny.isPending}>
                    Reject…
                  </Button>
                </div>
              </>
            ) : processingFor === null ? (
              <>
                <p className="text-muted-foreground mt-2 text-sm">
                  {didNotStart
                    ? "Processing didn't start. Try again in a minute."
                    : 'Klear suggests records from it; nothing is saved until you create them.'}
                </p>
                <div className="mt-4 flex flex-col items-start gap-1.5">
                  <Label htmlFor="process-trader">Trader</Label>
                  <Combobox
                    id="process-trader"
                    options={traderOptions}
                    value={traderId}
                    onChange={setPickedTraderId}
                    disabled={loadingTraders || !canEdit}
                    triggerLabel="Select trader"
                    placeholder="Search traders…"
                    className="w-full max-w-sm"
                  />
                </div>
                <div
                  className="mt-4 flex flex-wrap gap-2"
                  title={canEdit ? undefined : 'Ask a super admin or a CSM for edit access to process documents.'}
                >
                  <Button onClick={processDocument} disabled={!canEdit || traderName === null || process.isPending}>
                    Process document
                  </Button>
                  <Button variant="outline" onClick={() => setRejectOpen(true)} disabled={!canEdit || deny.isPending}>
                    Reject…
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h2 className="mt-2 flex items-center gap-2 text-base font-medium">
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden />
                  Processing the document for {processingFor}
                </h2>
                <p className="text-muted-foreground mt-1 text-sm">Usually under a minute.</p>
              </>
            )}
          </section>
        </div>
      </div>

      <RejectModal
        open={rejectOpen}
        title="Reject this document"
        isPending={deny.isPending}
        onOpenChange={setRejectOpen}
        onReject={rejectDocument}
      />
    </div>
  )
}
